/**
 * Numbered (offset) pagination hook — the sanctioned pattern for dense
 * admin / HR tables. Pairs with the `PaginationControls` component from
 * `components/lists/PaginationControls`.
 *
 * This is the numbered-page counterpart to `usePaginatedList` (the
 * sliding-window "Load more" hook). Both are documented in
 * docs/standards/pagination.md:
 *
 *   - usePaginatedList  → feed-like / employee-facing lists you scan
 *                         forward and rarely jump around in.
 *   - usePagedList      → admin tables where "jump to page 4" and
 *                         "show me 100 at once" beat clicking Load-more.
 *
 * Server contract (offset):
 *   GET /api/v1/<resource>?page=<1-based>&limit=<pageSize>
 *   → { data: [...], meta: { total, page, limit } }
 *
 *   The fetcher receives `{ page, pageSize }` and MUST return the rows for
 *   that page plus the server-reported `total` (needed to render the page
 *   count and "X–Y of Z"). Offset cost is negligible at this app's scale
 *   (single company, low-thousands of rows); see the standard doc for why
 *   the correctness caveats of offset paging don't bite here.
 *
 * Reset semantics:
 *   - Changing `pageSize` resets to page 1 (the old page number is
 *     meaningless under a new page size).
 *   - Changing the caller's `queryKey` (i.e. a filter/search change) resets
 *     to page 1 — you're looking at a different result set.
 *   - If `total` shrinks below the current page's first row (e.g. a filter
 *     narrows the set while you're on page 5), the hook clamps to the last
 *     valid page rather than showing an empty page.
 *
 * Typical caller:
 *
 *   const list = usePagedList<Employee>({
 *     queryKey: ['employees', filterArgs],
 *     fetcher: ({ page, pageSize }) =>
 *       listEmployeesPage({ ...filterArgs, page, limit: pageSize })
 *         .then((r) => ({ rows: r.data, total: r.meta.total })),
 *   })
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery, type QueryKey } from '@tanstack/react-query'

/** Page-size choices offered by the controls. Matches the product ask
 *  (10 / 20 / 50 / 100). Override per-surface via `pageSizeOptions`. */
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const
export const DEFAULT_PAGE_SIZE = 20

export interface PagedFetchArgs {
  /** 1-based page number. */
  page: number
  /** Rows per page. */
  pageSize: number
}

export interface PagedFetchResult<T, M = unknown> {
  rows: ReadonlyArray<T>
  /** Server-reported total across the (filtered) result set. Drives the
   *  page count and the "X–Y of Z" summary. */
  total: number
  /** Optional per-fetch metadata (summary cards, aggregate counts, …) carried
   *  alongside the rows. Because it rides inside the cached query data — rather
   *  than being written to component state as a fetch side effect — it survives
   *  a remount served from cache. Deriving such data via setState in the
   *  fetcher silently disappears on a cache hit (the fetcher never re-runs). */
  meta?: M
}

export interface UsePagedListOptions<T, M = unknown> {
  /** Filter-inclusive base key. The hook appends page + pageSize internally,
   *  so a filter change (new key) is what triggers the reset-to-page-1. */
  queryKey: QueryKey
  fetcher: (args: PagedFetchArgs) => Promise<PagedFetchResult<T, M>>
  /** Initial rows-per-page. Default 20. Must be one of `pageSizeOptions`. */
  initialPageSize?: number
  /** Override the offered page sizes. Default [10, 20, 50, 100]. */
  pageSizeOptions?: readonly number[]
  /** staleTime for the underlying query. Default 30s. */
  staleTime?: number
  /** Gate the fetch. Default true. Set false when a surface renders this
   *  hook alongside the sliding-window hook behind a feature flag and only
   *  one should actually fetch — keeps both hooks called (rules of hooks)
   *  while only the active pattern hits the network. */
  enabled?: boolean
}

export interface UsePagedListResult<T, M = unknown> {
  /** Rows for the current page (at most `pageSize`). */
  rows: ReadonlyArray<T>
  /** Per-fetch metadata from the current page's `PagedFetchResult.meta`.
   *  Undefined before the first fetch resolves. Rides inside the cached query
   *  data, so it survives a remount served from cache. */
  meta: M | undefined
  /** Current 1-based page. */
  page: number
  /** Current rows-per-page. */
  pageSize: number
  /** Server-reported total across the result set. */
  total: number
  /** Total number of pages (≥ 1 even when empty, so the footer reads
   *  "Page 1 of 1" rather than "Page 1 of 0"). */
  totalPages: number
  /** The offered page sizes, for the selector. */
  pageSizeOptions: readonly number[]
  /** 1-based index of the first row shown (0 when the list is empty). */
  rangeStart: number
  /** 1-based index of the last row shown (0 when the list is empty). */
  rangeEnd: number
  /** False on the first page. */
  canPrev: boolean
  /** False on the last page. */
  canNext: boolean
  /** True during the very first fetch (no data yet). */
  isLoading: boolean
  /** True when the current page fetch failed. */
  isError: boolean
  error: unknown
  /** True while any fetch is in flight (including page/size changes that
   *  keep the previous page visible). Drives a subtle "updating" affordance. */
  isFetching: boolean
  /** Jump to a specific 1-based page (clamped to [1, totalPages]). */
  setPage: (page: number) => void
  /** Advance one page (no-op on the last page). */
  nextPage: () => void
  /** Go back one page (no-op on the first page). */
  prevPage: () => void
  /** Change rows-per-page (resets to page 1). */
  setPageSize: (size: number) => void
}

function serializeKey(key: QueryKey): string {
  return JSON.stringify(key)
}

export function usePagedList<T, M = unknown>(
  options: UsePagedListOptions<T, M>,
): UsePagedListResult<T, M> {
  const {
    queryKey,
    fetcher,
    initialPageSize = DEFAULT_PAGE_SIZE,
    pageSizeOptions = PAGE_SIZE_OPTIONS,
    staleTime = 30_000,
    enabled = true,
  } = options

  const [page, setPageRaw] = useState(1)
  const [pageSize, setPageSizeRaw] = useState(initialPageSize)

  // Reset to page 1 when the caller's filter key changes. Tracked via a ref
  // of the serialized key so we only reset on an ACTUAL change, not on every
  // render (a fresh array/object literal each render would otherwise loop).
  const lastKeyRef = useRef(serializeKey(queryKey))
  const serializedKey = serializeKey(queryKey)
  if (serializedKey !== lastKeyRef.current) {
    lastKeyRef.current = serializedKey
    // setState during render is the React-idiomatic "derive from changed
    // input" pattern; React discards the in-flight render and re-runs with
    // page=1 before committing, so no stale page is ever painted.
    if (page !== 1) setPageRaw(1)
  }

  const query = useQuery<PagedFetchResult<T, M>>({
    queryKey: [...queryKey, 'paged', page, pageSize],
    queryFn: () => fetcher({ page, pageSize }),
    enabled,
    staleTime,
    // Keep the previous page on screen while the next one loads — avoids a
    // flash of empty table on every page/size change.
    placeholderData: (prev) => prev,
  })

  const rows = query.data?.rows ?? []
  const total = query.data?.total ?? 0
  const meta = query.data?.meta
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  // Clamp if a shrunk result set left us past the last page (e.g. a filter
  // narrowed the list while we were on page 5). Effect, not render-time: it
  // depends on the async `total`, which only arrives after the fetch.
  useEffect(() => {
    if (page > totalPages) setPageRaw(totalPages)
  }, [page, totalPages])

  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1
  const rangeEnd = total === 0 ? 0 : Math.min(page * pageSize, total)

  const setPage = useCallback(
    (next: number) => {
      setPageRaw((current) => {
        const clamped = Math.min(Math.max(1, Math.trunc(next)), totalPages)
        return clamped === current ? current : clamped
      })
    },
    [totalPages],
  )

  const nextPage = useCallback(() => setPage(page + 1), [page, setPage])
  const prevPage = useCallback(() => setPage(page - 1), [page, setPage])

  const setPageSize = useCallback((size: number) => {
    setPageSizeRaw(size)
    setPageRaw(1)
  }, [])

  return {
    rows,
    meta,
    page,
    pageSize,
    total,
    totalPages,
    pageSizeOptions,
    rangeStart,
    rangeEnd,
    canPrev: page > 1,
    canNext: page < totalPages,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isFetching: query.isFetching,
    setPage,
    nextPage,
    prevPage,
    setPageSize,
  }
}
